import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import _ from "lodash";

import { sortUsersByActivity } from "@/js/utils/userUtils";
import { arrayMove } from "@/js/utils/arrayUtils";
import * as windowUtils from "@/js/utils/windowUtils";
import p2pManager from "@/js/utils/p2pManager";

import ReportsDropdown from "./ReportsDropdown";
import Nav from "./Nav";
import EndClassButton from "./EndClassButton";
import OneActiveMonitorWarningModal from "./OneActiveMonitorWarningModal";
import ClassroomStartSession from "./ClassroomStartSession";
import MonitoringClassroomUser from "./MonitoringClassroomUser";
import { windowLimit } from "./RegularClassroom";
import P2PDisplayModal from "./P2PDisplayModal";
import AgentP2PModal from "./P2PDisplayModal/AgentP2PModal";
import ClassroomEditSession from "@/js/components/ClassroomEditSession";
import EditClassButton from "./EditClassButton";
import StartMonitoringButton from "./StartMonitoringButton";
import { generateSessionStatusTxt } from "@/js/utils/timeUtils";
import { TabBar } from "./TabBar";
import { FocusAndFlexibleGridBar } from "./RegularClassroom/FocusAndFlexibleGridBar";
import { SeatPlanView } from "@/components/SeatPlan/SeatPlanView";
import { SeatPlanEmptyState } from "@/components/SeatPlan/SeatPlanEmptyState";

import useStore from "@/stores/index";
import { getClassroomStore } from "@/stores/classroomStore";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useSeatPlanStore } from "@/stores/v2/seatPlanStore";
import { useClassroomSessionStore } from "@/stores/v2/classroomSessionStore";
import { useClassroomPolicyStore } from "@/stores/v2/classroomPolicyStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { useAccountSettingsUse24Hour } from "@/query/accountSettingsQuery";
import { ModifyStudentsModal } from "@/components/ModifyStudentsModal";
import { Box, useDisclosure } from "@chakra-ui/react";
import { getFeatures } from "@/stores/classwizeConfigStore";
import { ActionButton } from "./Classrooms/ActionButton";
import { tileWidthPxFromScale, useClassroomLayoutStore } from "@/stores/v2/classroomLayoutStore.ts";
import { sortSeatRosterIds } from "@/components/SeatPlan/seatLayout";
import { useTranslation } from "react-i18next";

const MonitoringClassroom = (props) => {
	const { t } = useTranslation();
	const store = useStore((state) => state);
	const classwizeFeatures = getFeatures(store);

	const [activeClassroom] = useState(useUserSessionStore.getState().getActiveGroup());
	const [classExpiryStr, setClassExpiryStr] = useState("");
	const [view, setView] = useState("screens"); // enum of ('screens', 'tabs')
	const [showAllWindows, setShowAllWindows] = useState(false);
	const [classroomPolicy, setClassroomPolicy] = useState(useClassroomPolicyStore.getState());

	// modals
	const modifyStudentsModal = useDisclosure();
	const [showStartClassModal, setShowStartClassModal] = useState(false);
	const [showEditClassModal, setShowEditClassModal] = useState(false);
	const [showMonitoringClassActiveWarning, setShowMonitoringClassActiveWarning] = useState(false);
	const [p2pModalOpen, setP2pModalOpen] = useState(false);
	// errors
	const [startClassSessionError, setStartClassSessionError] = useState(false);

	const [p2pModalUser, setP2pModalUser] = useState();
	const [p2pAgentId, setP2pAgentId] = useState();
	const [p2pChromeWindowId, setP2pChromeWindowId] = useState();
	const [p2pModalSelectedTab, setP2pModalSelectedTab] = useState();
	const [p2pModalFollowActiveTab, setP2pModalFollowActiveTab] = useState(false);

	const [users, setUsers] = useState(props.users);
	const [classroom, setClassroom] = useState(props.classroom);

	const defaultTileScale = useClassroomLayoutStore((state) => state.defaultTileScale);
	const [tabItemLimit, setTabItemLimit] = React.useState(5); // variable tab items to display on student tile

	const selectedClassroomStore = useSelectedClassroomStore();
	const selectedClassroomStudents = useSelectedClassroomStore((s) => s.students);
	const use24Hour = useAccountSettingsUse24Hour();

	// Seat Plan view state. The seat-plan store is a global singleton shared with regular classes,
	// so monitor classes hydrate/reconcile the same way and clean up unsaved arrangement on unmount.
	const hydrateSeatPlan = useSeatPlanStore((s) => s.hydrate);
	const isSeatPlanHydrated = useSeatPlanStore((s) => s.hydratedClassroomStableId === props.classroom.stableIdV1);
	const seatRosterStudents = selectedClassroomStudents;
	const isDraggingSeats = useSeatPlanStore((s) => s.activeId !== null);
	const hasUnsavedArrangement = useSeatPlanStore((s) => s.hasUnsavedArrangement);
	const isMovingSeat = useSeatPlanStore((s) => s.pendingMoveIndex !== null);

	useEffect(() => {
		return () => useSeatPlanStore.getState().resetTransientArrangement();
	}, []);

	// Match the seat plan tile aspect ratio to the default monitor view. Both the default tiles and
	// the seat tiles derive their height from the same tile scale, so giving the seat plan the same
	// width as the default view yields the same (more square) proportions. Re-seed whenever the
	// default tile scale changes — including after the persisted layout hydrates it — so the seat
	// plan always tracks the default view's scale instead of locking to a stale initial value.
	useEffect(() => {
		useSeatPlanStore.getState().setCardWidth(tileWidthPxFromScale(defaultTileScale));
	}, [defaultTileScale]);

	// Reconcile slots against roster changes (newcomers fill empty seats, departed students get nulled),
	// preserving the hydrated columns/rows/cardWidth. Skipped while dragging or with unsaved arrangement
	// so in-progress moves are not reset, and until GetClass has seeded this class's layout.
	useEffect(() => {
		if (isDraggingSeats || isMovingSeat || hasUnsavedArrangement || !isSeatPlanHydrated) return;
		const rosterIds = sortSeatRosterIds(seatRosterStudents ?? []);
		const { slots, columns, rows, cardWidth } = useSeatPlanStore.getState();
		hydrateSeatPlan({ slots, columns, rows, cardWidth }, rosterIds);
	}, [seatRosterStudents, isDraggingSeats, isMovingSeat, hasUnsavedArrangement, isSeatPlanHydrated, hydrateSeatPlan]);

	useEffect(() => {
		setUsers(props.users);
		setClassroom(props.classroom);
		setClassExpiryStr(generateSessionStatusTxt(props.classroom.id, false, use24Hour));
	}, [props, use24Hour]);

	useEffect(() => {
		_.debounce((users) => {
			p2pManager.checkUsersToFetchToken(users);
		}, 3000);
	}, [users]);

	useEffect(() => {
		setClassExpiryStr(generateSessionStatusTxt(props.classroom.id, false, use24Hour));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.isClassStarted, use24Hour]);

	// didMount
	useEffect(() => {
		const onChangeClassroomPolicy = (state) => setClassroomPolicy(state);

		const onChangeClassroomSessionStore = (state) => {
			setShowStartClassModal(state.showStartClassModal);
			setShowEditClassModal(state.showEditClassModal);
			setStartClassSessionError(state.startClassSessionError);
			setClassExpiryStr(generateSessionStatusTxt(props.classroom.id, false, use24Hour));
			setShowMonitoringClassActiveWarning(state.showMonitoringClassActiveWarning);
		};

		const unsubFromClassroomSessionStore = useClassroomSessionStore.subscribe(
			(state) => state,
			(newState) => {
				onChangeClassroomSessionStore(newState);
			}
		);

		const unsubClassroomPolicyStore = useClassroomPolicyStore.subscribe(
			(state) => state,
			(newState) => onChangeClassroomPolicy(newState)
		);

		return () => {
			unsubClassroomPolicyStore();
			unsubFromClassroomSessionStore();
		};
	}, [props.classroom.id, use24Hour]);

	const toggleShowWindows = () => setShowAllWindows(!showAllWindows);

	const openP2pModal = (user, tabId, peerAgentId, chromeWindowId, followActiveTab = false) => {
		setP2pModalUser(user.username);
		setP2pModalSelectedTab(tabId);
		setP2pAgentId(peerAgentId);
		setP2pChromeWindowId(chromeWindowId);
		setP2pModalOpen(true);
		setP2pModalFollowActiveTab(followActiveTab);
	};

	const closeP2pModal = () => {
		setP2pModalOpen(false);
	};

	const renderP2pModal = () => {
		const user = users.find((user) => user.username === p2pModalUser);
		const agent = user.screenshots.find((screen) => screen.chrome_id === p2pAgentId && screen.chrome_window_id === p2pChromeWindowId);
		const platform = windowUtils.getPlatformType(agent);
		const chrome = platform === "Chromebook";

		if (!agent || !user || (!p2pModalSelectedTab && chrome)) return;
		if (!chrome && !store.featureToggles.data["agent-p2p-screenshots"]) return;

		if (chrome) {
			const tabs = [
				{
					tab_id: agent.tab_id,
					favIcon: agent.favicon,
					tabUrl: agent.url,
					title: agent.title,
				},
				...agent.background_tabs,
			];
			const startTabIndex = tabs.findIndex((tab) => tab.tab_id === p2pModalSelectedTab);

			return (
				<P2PDisplayModal
					isMonitoringClass={true}
					isStudentClaimed={false}
					open={p2pModalOpen}
					close={closeP2pModal}
					user={user}
					startTab={tabs[startTabIndex]}
					windowId={p2pChromeWindowId}
					startTabIndex={startTabIndex}
					peerAgentId={p2pAgentId}
					activeTab={agent}
					tabs={tabs}
					classroom={classroom}
					followActiveTab={p2pModalFollowActiveTab}
					platform={platform}
				/>
			);
		} else {
			return (
				<AgentP2PModal
					isMonitoringClass={true}
					isStudentClaimed={false}
					open={p2pModalOpen}
					close={closeP2pModal}
					user={user}
					activeTab={agent}
					windowId={p2pChromeWindowId}
					peerAgentId={p2pAgentId}
					classroom={classroom}
					platform={platform}
				/>
			);
		}
	};

	let totalWindows = 0;
	let shownWindows = 0;
	let extraWindows = 0;
	const isClassActive = getClassroomStore(store).isClassSessionActiveV2(classroom.id);
	let sortedUsers = sortUsersByActivity(users, classroomPolicy.locked_users, true);
	sortedUsers = selectedClassroomStore.sortUsersByPin(sortedUsers);
	// An auto-sorted seat plan follows this order, so monitor seats track activity like the tiles do.
	// Only while the class runs: screenshots can arrive beforehand, and hoisting those students ahead
	// of the rest makes a stopped class look arbitrarily ordered instead of sorted by name.
	const activityOrderedIds = isClassActive ? sortedUsers.map((u) => u.qoriaUserId).filter((id) => !!id) : undefined;

	const cardsByStudent = {};
	for (const user of sortedUsers) {
		let ret = [];
		const uniqueDevices = Array.from(new Set(user.screenshots.map((windowItem) => windowItem.chrome_id))).sort();
		for (const windowData of user.screenshots) {
			ret.push(
				<MonitoringClassroomUser
					key={user.username + "_" + windowData.chrome_id + "_" + windowData.chrome_window_id}
					user={user}
					view={view}
					deviceIndex={uniqueDevices.indexOf(windowData.chrome_id) + 1}
					windowData={windowData}
					isClassActive={isClassActive}
					classroom={classroom}
					openP2pModal={openP2pModal}
					tabItemLimit={tabItemLimit}
				/>
			);
		}

		if (ret.length === 0) {
			ret.push(
				<MonitoringClassroomUser
					key={user.username}
					user={user}
					view={view}
					screenshots={{}}
					isClassActive={isClassActive}
					classroom={classroom}
					tabItemLimit={tabItemLimit}
				/>
			);
		}
		const knownDevices = [];
		let lastUniqueDeviceIndex = 0;
		for (const screenIndex in ret) {
			const currentScreen = ret[screenIndex];
			if (currentScreen.props.deviceIndex && knownDevices.indexOf(currentScreen.props.deviceIndex) < 0) {
				knownDevices.push(currentScreen.props.deviceIndex);
				arrayMove(ret, screenIndex, lastUniqueDeviceIndex);
				lastUniqueDeviceIndex++;
			}
		}
		// Capture the full (unsliced) window list per student for the seat plan view, which renders
		// one seat per student rather than one tile per window.
		if (user.qoriaUserId) {
			cardsByStudent[user.qoriaUserId] = ret;
		}
		totalWindows += ret.length;
		if (!showAllWindows && ret.length >= windowLimit) {
			ret = ret.slice(0, windowLimit);
		}
		if (showAllWindows && ret.length >= windowLimit) {
			extraWindows += ret.length - windowLimit;
		}
		shownWindows += ret.length;
	}

	return (
		<div>
			<Nav selectedClass={classroom} />
			<TabBar isActive={isClassActive} />
			<ul className="ctxbar--classroomPage">
				<li
					className={isClassActive ? (view === "screens" ? "ctxbar-toggle--active" : "ctxbar-toggle") : "ctxbar-toggle-hide"}
					onClick={() => setView("screens")}
				>
					{t("classroomDetails.screenView")}
				</li>
				<li
					className={isClassActive ? (view === "tabs" ? "ctxbar-toggle--active--list" : "ctxbar-toggle--list") : "ctxbar-toggle-hide"}
					onClick={() => setView("tabs")}
				>
					{t("classroomDetails.listView")}
				</li>
				<li>
					{isClassActive ? (
						<EditClassButton
							classroomName={activeClassroom}
							classExpiryStr={classExpiryStr}
							isMonitoringClass={true}
							screenView={true}
							classActive={isClassActive}
							classroomId={classroom.id}
						>
							{t("classroomDetails.monitoringUntil", { time: classExpiryStr })}
						</EditClassButton>
					) : (
						<StartMonitoringButton classroomName={activeClassroom} monitoring={true} disabled={users.length === 0} />
					)}
				</li>

				{isClassActive && (
					<li>
						<EndClassButton classroomName={activeClassroom} isMonitoringClass={true} />
					</li>
				)}

				<ul className="ctxbar_alignRight">
					<ReportsDropdown students={users} />
					<Box mx={1}></Box>
					{classwizeFeatures.includes("add_user") && (
						<ActionButton label={t("classroomDetails.modifyStudents")} icon="fa-users" handleClick={() => modifyStudentsModal.onOpen()} />
					)}
				</ul>
			</ul>
			{isClassActive && <div className="MonitoringClassroom_monitorOnlyBanner">{t("classroomDetails.monitorOnlyMode")}</div>}

			<FocusAndFlexibleGridBar
				studentCount={users.length}
				setTabItemLimit={setTabItemLimit}
				appliedFocuses={[]}
				isUpdatingFocus={false}
				endFocus={() => {}}
				bubbleUpdatedFocus={() => {}}
				activityOrderedIds={activityOrderedIds}
			/>

			{(seatRosterStudents ?? []).length === 0 ? (
				<SeatPlanEmptyState />
			) : isSeatPlanHydrated ? (
				<SeatPlanView
					cardsByStudent={cardsByStudent}
					isClassActive={isClassActive}
					selectionEnabled={false}
					tilesHaveActionRow={false}
					activityOrderedIds={activityOrderedIds}
					students={(seatRosterStudents ?? []).map((s) => ({
						qoriaUserId: s.qoriaUserId,
						stableIdV1: s.stableIdV1,
						username: s.username,
						firstName: s.firstName,
						lastName: s.lastName,
					}))}
				/>
			) : null}
			<ModifyStudentsModal
				isOpen={modifyStudentsModal.isOpen}
				onClose={modifyStudentsModal.onClose}
				classroom={classroom}
				students={users}
				isMonitoringClass={true}
			/>
			<ClassroomStartSession
				open={showStartClassModal}
				onStartError={startClassSessionError}
				studentCount={users.length}
				isMonitoringClass
				inClassroom
			/>
			<ClassroomEditSession
				open={showEditClassModal}
				onExtendError={startClassSessionError}
				durationPlaceholder={classExpiryStr}
				isMonitoringClass
				inClassroom
			/>
			<OneActiveMonitorWarningModal
				open={showMonitoringClassActiveWarning}
				onClose={() => useClassroomSessionStore.getState().handleCloseMonitoringWarningModal()}
				existingClassname={activeClassroom.name}
				onStart={(expiry) => {
					useClassroomSessionStore.getState().handleStartClassSession({
						classroomId: classroom.id,
						expiry: expiry,
						force: true,
						inClassroom: true,
					});
				}}
			/>
			{p2pModalOpen && renderP2pModal()}
			{totalWindows - shownWindows > 0 || extraWindows > 0 ? (
				<div className="RegularClassroom_showAllWindowsButton" onClick={toggleShowWindows}>
					{showAllWindows
						? t("classroomDetails.hideExtraScreens", { count: extraWindows })
						: t("classroomDetails.showExtraScreens", { count: totalWindows - shownWindows })}
				</div>
			) : null}
		</div>
	);
};

MonitoringClassroom.propTypes = {
	users: PropTypes.array.isRequired,
	classroom: PropTypes.object,
	isClassStarted: PropTypes.bool,
};

export default MonitoringClassroom;
